import { Builder } from 'react-native-jala-orm/src/model/Builder';
import { Builder as QueryBuilder } from 'react-native-jala-orm/src/query/Builder';
import _ from 'lodash';
import Expression from 'react-native-jala-orm/src/query/Expression';
import moment from 'moment';
import SyncableModel from './SyncableModel';
import Cycle from './Cycle';
import Pond from './Pond';
import Parameter from './Parameter';
import FarmParameter from './FarmParameter';

class Measurement extends SyncableModel {
  static name = 'Measurement'

  _fillable = [
    'id', 'pond_id', 'cycle_id', 'parameter_id',
    'value', 'measured_at',
    'created_at', 'updated_at', 'deleted_at', 'synced_at',
  ]

  _casts = {
    value: 'float'
  }

  static _parents = ['pond'];

  static endpoint = '/measurements'

  static allowReplaceUpdate = true

  static originalParameters = []

  static parameters = []

  _appends = [
    'is_can_be_pushed',
    'is_modified',
    'is_not_synced',
    'measured_date',
    'measured_time',
  ]

  static _boot() {
    super._boot();

    if (!Measurement.parameters.length) {
      Parameter.fetch({ sort: 'number' })
        .then((parameters) => {
          Measurement.originalParameters = parameters;
          Measurement.parameters = [...Measurement.originalParameters];
        });
    }

    this.addGlobalScope('global', (query: Builder) => {
      if (_.isEmpty(query.getQuery().columns)) {
        query.select([query.qualifyColumn('*')]);
      }

      const pond = (new Pond()).setTable(`${query.getModel().getTable()}_ponds`);
      query.leftJoin(
        `${(new Pond()).getTable()} as ${pond.getTable()}`,
        pond.getQualifiedKeyName(), query.qualifyColumn(pond.getForeignKey())
      );
      const parameter = (new Parameter()).setTable(`${query.getModel().getTable()}_parameters`);
      query.leftJoin(
        `${(new Parameter()).getTable()} as ${parameter.getTable()}`,
        parameter.getQualifiedKeyName(), query.qualifyColumn(parameter.getForeignKey())
      );
      const cycle = (new Cycle()).setTable(`${query.getModel().getTable()}_cycles`);
      query.leftJoin(
        `${(new Cycle()).getTable()} as ${cycle.getTable()}`,
        cycle.getQualifiedKeyName(), query.qualifyColumn(cycle.getForeignKey())
      );
    });

    this.addGlobalScope('join_farm_parameter', (query: Builder) => {
      const pond = (new Pond()).setTable(`${query.getModel().getTable()}_ponds`);
      const parameter = (new Parameter()).setTable(`${query.getModel().getTable()}_parameters`);
      const farmParameter = (new FarmParameter()).setTable(`${query.getModel().getTable()}_farm_parameter`);
      query.leftJoin(
        `${(new FarmParameter()).getTable()} as ${farmParameter.getTable()}`,
        (q: QueryBuilder) => q.whereColumn(
          parameter.getQualifiedKeyName(), farmParameter.qualifyColumn(parameter.getForeignKey())
        ).whereColumn(pond.qualifyColumn('farm_id'), farmParameter.qualifyColumn('farm_id'))
      );
    });
  }

  qualifyColumn(column: string) {
    if (column === '*') {
      return new Expression(
        `${this.getTable()}.*, `
          + `${this.qualifyColumn('age')} as age, `
          + `${this.qualifyColumn('measured_date')} as measured_date, `
          + `${this.qualifyColumn('measured_time')} as measured_time, `
          + `${this.qualifyColumn('anomaly')} as anomaly`
      );
    }
    if (column === 'age') {
      const cycle = (new Cycle()).setTable(`${this.getTable()}_cycles`);
      return new Expression(
        `(CASE WHEN ${cycle.getQualifiedKeyName()} IS NOT NULL THEN `
          + 'MIN('
            + 'CAST(('
              + `JULIANDAY(${this.qualifyColumn('measured_date')}) - `
              + `JULIANDAY(${cycle.qualifyColumn('started_at')})) AS INTEGER`
            + `) + COALESCE(${cycle.qualifyColumn('initial_age')}, 0), `
          + `COALESCE(${cycle.qualifyColumn('target_cultivation_day')}, 120)`
        + ') ELSE null END)'
      );
    }
    if (column === 'measured_date') {
      return new Expression(`SUBSTR(${this.qualifyColumn('measured_at')}, 1, 10)`);
    }
    if (column === 'measured_time') {
      return new Expression(`SUBSTR(${this.qualifyColumn('measured_at')}, 12, 8)`);
    }
    if (column === 'anomaly') {
      return new Expression('('
          + 'CASE '
            + `WHEN ${this.qualifyColumn('value')} < ${this.qualifyColumn('low_limit')} OR `
              + `${this.qualifyColumn('high_limit')} < ${this.qualifyColumn('value')} THEN 1 `
            + 'ELSE 0 '
          + 'END'
        + ')');
    }
    if (['lowest_limit', 'low_limit', 'high_limit', 'highest_limit'].includes(column)) {
      const parameter = (new Parameter()).setTable(`${this.getTable()}_parameters`);
      const farmParameter = (new FarmParameter()).setTable(`${this.getTable()}_farm_parameter`);
      return new Expression('('
          + 'CASE '
            + `WHEN ${farmParameter.getQualifiedKeyName()} IS NOT NULL `
            + `THEN ${farmParameter.qualifyColumn(column)} ELSE ${parameter.qualifyColumn(column)} `
          + 'END'
        + ')');
    }
    return super.qualifyColumn(column);
  }

  /**
   * @param {Builder} query
   * @return {Builder}
   */
  scopeToMulti(query: Builder) {
    query.withoutGlobalScope('join_farm_parameter');
    query.select([
      query.qualifyColumn('id'),
      query.qualifyColumn('pond_id'),
      `${query.qualifyColumn('cycle_id')} as cycle_id`,
      query.qualifyColumn('measured_at'),
      new Expression(`${query.qualifyColumn('age')} as age`),
      new Expression(`${query.qualifyColumn('measured_date')} as measured_date`),
      new Expression(`${query.qualifyColumn('measured_time')} as measured_time`),
    ]);

    const parentScope = (q: Builder) => {
      const wheresParent = q.getQuery().wheres
        .filter((where) => where.column
                  && !(where.column instanceof Expression)
                  && ['pond_id', 'cycle_id', 'parameter_id'].reduce((carry, value) => carry || (where.column.includes(value)), false));
      const measurementT = (new Measurement()).setTable(`${q.getModel().getTable()}_t`);
      q.leftJoin(
        `${(new Measurement()).getTable()} as ${measurementT.getTable()}`,
        (q1: QueryBuilder) => {
          q1.whereColumn(measurementT.qualifyColumn('measured_at'), q.qualifyColumn('measured_at'))
            .whereColumn(measurementT.qualifyColumn('pond_id'), q.qualifyColumn('pond_id'))
            .whereNull(measurementT.qualifyColumn('deleted_at'))
            .whereColumn(measurementT.getQualifiedKeyName(), '<', q.getModel().getQualifiedKeyName());
          if (wheresParent && wheresParent.length) {
            wheresParent.forEach((where) => {
              if (where.type === 'Basic') q1.where(measurementT.qualifyColumn(measurementT._removeTableFromKey(where.column)), where.operator, where.value, where.boolean);
              else if (where.type === 'In') q1.whereIn(measurementT.qualifyColumn(measurementT._removeTableFromKey(where.column)), where.values);
            });
          }
        }
      );
      q.whereNull(measurementT.getQualifiedKeyName());

      q.getModel().constructor.parameters.forEach((parameter: Parameter) => {
        const measurement = (new Measurement()).setTable(`${parameter.code}s`);
        q.leftJoin(
          `${(new Measurement()).getTable()} as ${measurement.getTable()}`,
          (q1: QueryBuilder) => {
            q1.whereColumn(measurement.qualifyColumn('measured_at'), q.qualifyColumn('measured_at'))
              .whereColumn(measurement.qualifyColumn('pond_id'), q.qualifyColumn('pond_id'))
              .whereNull(measurement.qualifyColumn('deleted_at'))
              .where(measurement.qualifyColumn('parameter_id'), parameter.getKey());
          }
        );
        q.addSelect(`${measurement.qualifyColumn('value')} as ${parameter.code}`);

        const parameterQualifyColumn = (column) => {
          const pond = (new Pond()).setTable(`${q.getModel().getTable()}_ponds`);
          const parameterFarmParameter = (new FarmParameter()).setTable(`${parameter.code}s_farm_parameter`);
          const parameterFarmParameterQuery = FarmParameter.query()
            .setModel(parameterFarmParameter)
            .withoutGlobalScopes()
            .table((new FarmParameter()).getTable(), parameterFarmParameter.getTable())
            .whereColumn(parameterFarmParameter.qualifyColumn('farm_id'), pond.qualifyColumn('farm_id'))
            .whereColumn(parameterFarmParameter.qualifyColumn('parameter_id'), measurement.qualifyColumn('parameter_id'))
            .toBase();
          return new Expression('('
            + 'CASE '
              + `WHEN (${parameterFarmParameterQuery.select([parameterFarmParameter.getQualifiedKeyName()]).toSql()}) IS NOT NULL `
                + `THEN (${parameterFarmParameterQuery.select([parameterFarmParameter.qualifyColumn(column)]).toSql()}) ELSE ${parameter.getAttribute(column) || 'NULL'} `
            + 'END'
          + ')');
        };

        q.addSelect(new Expression('('
          + 'CASE '
            + `WHEN ${measurement.qualifyColumn('value')} IS NULL THEN NULL `
            + `WHEN ${measurement.qualifyColumn('value')} < ${parameterQualifyColumn('low_limit')} OR `
              + `${parameterQualifyColumn('high_limit')} < ${measurement.qualifyColumn('value')} THEN 1 `
            + 'ELSE 0 '
          + 'END'
        + `) as ${parameter.code}_anomaly`));

        q.whereNotExists((q1: QueryBuilder) => {
          const parameterMeasurementT = (new Measurement()).setTable(`${measurement.getTable()}_t`);
          return q1.selectRaw(1)
            .table((new Measurement()).getTable(), parameterMeasurementT.getTable())
            .where(parameterMeasurementT.qualifyColumn('parameter_id'), parameter.getKey())
            .whereColumn(parameterMeasurementT.qualifyColumn('pond_id'), measurement.qualifyColumn('pond_id'))
            .whereColumn(parameterMeasurementT.qualifyColumn('measured_at'), measurement.qualifyColumn('measured_at'))
            .whereNull(parameterMeasurementT.qualifyColumn('deleted_at'))
            .whereColumn(parameterMeasurementT.getQualifiedKeyName(), '>', measurement.getQualifiedKeyName());
        });
      });
      q.addSelect(new Expression(`(${
        q.getModel().constructor.parameters.map((parameter: Parameter) => {
          const measurement = (new Measurement()).setTable(`${parameter.code}s`);
          return '('
            + 'CASE '
              + `WHEN ${measurement.getQualifiedKeyName()} IS NOT NULL AND (`
                + `${measurement.qualifyColumn('synced_at')} IS NULL OR `
                + `${measurement.qualifyColumn('deleted_at')} IS NOT NULL OR `
                + `${measurement.qualifyColumn('synced_at')} < ${measurement.qualifyColumn('updated_at')}`
              + ') THEN 1 '
              + 'ELSE 0 '
            + 'END'
          + ')';
        }).join(' OR ')
      }) as modified`));
      q.whereExists((q1: QueryBuilder) => {
        const parameterExistsMeasurementT = (new Measurement()).setTable(_.uniqueId('t'));
        return q1.selectRaw(1)
          .table((new Measurement()).getTable(), parameterExistsMeasurementT.getTable())
          .whereIn(parameterExistsMeasurementT.qualifyColumn('parameter_id'), q.getModel().constructor.parameters.map((p) => p.getKey()))
          .whereColumn(parameterExistsMeasurementT.qualifyColumn('measured_at'), q.qualifyColumn('measured_at'))
          .whereColumn(parameterExistsMeasurementT.qualifyColumn('pond_id'), q.qualifyColumn('pond_id'))
          .whereNull(parameterExistsMeasurementT.qualifyColumn('deleted_at'));
      });

      return q;
    };

    if (query.getModel().constructor.name === 'MultiMeasurement') parentScope(query);
    else query.withGlobalScope(_.uniqueId('s'), parentScope);

    return query;
  }

  /**
   * @param {Builder} query
   * @param {Pond|null} pond
   * @param {null} parameter
   * @return Builder
   */
  scopeLatestByPond(query: Builder, pond: Pond = null, parameter = null) {
    query.withGlobalScope(_.uniqueId('t'), (q: Builder) => {
      q.whereNotExists((q1: QueryBuilder) => {
        const measurementT = (new Measurement()).setTable(`${query.getModel().getTable()}_${_.uniqueId('t')}`);
        q1.selectRaw(1).table((new Measurement()).getTable(), measurementT.getTable())
          .whereColumn(measurementT.qualifyColumn('pond_id'), q.qualifyColumn('pond_id'))
          .whereColumn(measurementT.qualifyColumn('measured_at'), q.qualifyColumn('measured_at'))
          .whereIn(measurementT.qualifyColumn('parameter_id'), q.getModel().constructor.parameters.map((p) => p.getKey()))
          .whereNull(measurementT.qualifyColumn('deleted_at'))
          .whereColumn(measurementT.getQualifiedKeyName(), '<', q.getModel().getQualifiedKeyName());
        if (pond && pond.exists) q1.where(measurementT.qualifyColumn('pond_id'), pond.getKey());

        return q1;
      });

      q.whereNotExists((q1: QueryBuilder) => {
        const measurementT = (new Measurement()).setTable(`${query.getModel().getTable()}_${_.uniqueId('t')}`);
        q1.selectRaw(1).table((new Measurement()).getTable(), measurementT.getTable())
          .whereColumn(measurementT.qualifyColumn('pond_id'), q.qualifyColumn('pond_id'))
          .whereColumn(measurementT.qualifyColumn('measured_at'), '>', q.qualifyColumn('measured_at'))
          .whereNull(measurementT.qualifyColumn('deleted_at'))
          .whereIn(measurementT.qualifyColumn('parameter_id'), q.getModel().constructor.parameters.map((p) => p.getKey()));
        if (pond && pond.exists) q1.where(measurementT.qualifyColumn('pond_id'), pond.getKey());

        return q1;
      });

      q.whereIn(q.qualifyColumn('parameter_id'), q.getModel().constructor.parameters.map((p) => p.getKey()));

      return q;
    });

    return query;
  }

  scopeToDate(query: Builder) {
    query.withoutGlobalScope('join_farm_parameter');
    query.select([
      query.getModel().getQualifiedKeyName(),
      query.qualifyColumn('pond_id'),
      query.qualifyColumn('cycle_id'),
      new Expression(`${query.qualifyColumn('age')} as age`),
      new Expression(`${query.qualifyColumn('measured_date')} as measured_date`)
    ]);

    query.withGlobalScope(_.uniqueId('t'), (q: Builder) => {
      q.whereNotExists((q1: QueryBuilder) => {
        const measurementT = (new Measurement()).setTable(`${query.getModel().getTable()}_${_.uniqueId('t')}`);
        q1.selectRaw(1).table((new Measurement()).getTable(), measurementT.getTable())
          .whereColumn(measurementT.qualifyColumn('pond_id'), q.qualifyColumn('pond_id'))
          .whereColumn(measurementT.qualifyColumn('measured_date'), q.qualifyColumn('measured_date'))
          .whereNull(measurementT.qualifyColumn('deleted_at'))
          .whereIn(measurementT.qualifyColumn('parameter_id'), q.getModel().constructor.parameters.map((p) => p.getKey()))
          .whereColumn(measurementT.getQualifiedKeyName(), '<', q.getModel().getQualifiedKeyName());

        return q1;
      });

      q.whereIn(q.qualifyColumn('parameter_id'), q.getModel().constructor.parameters.map((p) => p.getKey()));
    });

    return query
  }

  /**
   * @param {String} value
   * @return {String}
   */
  getMeasuredDateAttribute(value) {
    if (value) return value;
    if (this.exists) {
      this._attributes['measured_date'] = this._attributes['measured_at'] ? moment(this._attributes['measured_at']).format('YYYY-MM-DD') : null;
      this.syncOriginalAttribute('measured_date');
      return this._attributes['measured_date'];
    }

    return this._attributes['measured_at'] ? moment(this._attributes['measured_at']).format('YYYY-MM-DD') : null;
  }

  /**
   * @param {String} value
   */
  setMeasuredDateAttribute(value) {
    const date = moment(value || undefined, 'YYYY-MM-DD', false);
    this._attributes = {
      ...this._attributes,
      measured_at: date.format('YYYY-MM-DD') + moment(this._attributes.measured_at || undefined).format('THH:mm:ssZ')
    };
    if (this.exists) {
      this._attributes = {
        ...this._attributes,
        measured_date: date.format('YYYY-MM-DD')
      };
      this.syncOriginalAttribute('measured_date');
    }
    return this;
  }

  /**
   * @param {String} value
   * @return {String}
   */
  getMeasuredTimeAttribute(value) {
    if (value) return value;
    if (this.exists) {
      this._attributes['measured_time'] = this._attributes['measured_at'] ? moment(this._attributes['measured_at']).format('HH:mm:ss') : null;
      this.syncOriginalAttribute('measured_time');
      return this._attributes['measured_time'];
    }

    return this._attributes['measured_at'] ? moment(this._attributes['measured_at']).format('HH:mm:ss') : null;
  }

  /**
   * @param {String} value
   */
  setMeasuredTimeAttribute(value) {
    const time = moment(value || undefined, 'HH:mm:ss', false);
    this._attributes = {
      ...this._attributes,
      measured_at: moment(this._attributes.measured_at || undefined).format('YYYY-MM-DD') + time.format('THH:mm:ssZ')
    };
    if (this.exists) {
      this._attributes = {
        ...this._attributes,
        measured_time: time.format('HH:mm:ss')
      };
      this.syncOriginalAttribute('measured_time');
    }
    return this;
  }

  relationPond() {
    return Pond.query().withoutGlobalScope('soft_delete').whereKey(this.pond_id);
  }

  static toDate() {
    return (new Measurement()).scopeToDate(this.query())
  }
}

Parameter.Measurement = Measurement;

export default Measurement;
